import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import API_URL, { BASE_URL } from '../../../../config';
import './VideoWatermark.css';

function VideoWatermark({ isOpen, onClose, mediaFile, mediaPreview, onSave }) {
    const [watermarkFile, setWatermarkFile] = useState(null);
    const [watermarkPreview, setWatermarkPreview] = useState(null);
    const [watermarkPosition, setWatermarkPosition] = useState('bottom-right');
    const [watermarkSize, setWatermarkSize] = useState(100);
    const [watermarkOpacity, setWatermarkOpacity] = useState(80);
    const [videoDimensions, setVideoDimensions] = useState({ width: 0, height: 0 });

    // Serbest konumlandırma - piksel olarak saklıyoruz
    const [dragPos, setDragPos] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

    // Logo doğal boyutu (banner GIF algılama için)
    const [logoNaturalSize, setLogoNaturalSize] = useState({ width: 0, height: 0 });

    // Markalar için state
    const [brands, setBrands] = useState([]);
    const [loadingBrands, setLoadingBrands] = useState(false);
    const [selectedBrandLogo, setSelectedBrandLogo] = useState(null);

    const videoRef = useRef(null);
    const iconInputRef = useRef(null);
    const containerRef = useRef(null);
    const videoWrapperRef = useRef(null);
    const overlayRef = useRef(null);

    // Markaları yükle
    useEffect(() => {
        if (isOpen) {
            fetchBrands();
        }
    }, [isOpen]);

    const fetchBrands = async () => {
        try {
            setLoadingBrands(true);
            const response = await axios.get(`${API_URL}/brands`);
            const activeBrands = (response.data.brands || []).filter(
                brand => brand.is_active && (brand.logo_dark_url || brand.logo_light_url || brand.gif_url)
            );
            setBrands(activeBrands);
        } catch (error) {
            console.error('Brands fetch error:', error);
        } finally {
            setLoadingBrands(false);
        }
    };

    // ESC tuşu
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.keyCode === 27 && isOpen) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEsc);
        }

        return () => document.removeEventListener('keydown', handleEsc);
    }, [isOpen, onClose]);

    // Modal kapandığında state'leri sıfırla
    useEffect(() => {
        if (!isOpen) {
            setWatermarkFile(null);
            setWatermarkPreview(null);
            setWatermarkPosition('bottom-right');
            setWatermarkSize(100);
            setWatermarkOpacity(80);
            setSelectedBrandLogo(null);
            setDragPos(null);
            setIsDragging(false);
            setLogoNaturalSize({ width: 0, height: 0 });
        }
    }, [isOpen]);

    // Video metadata yüklendiğinde
    const handleVideoLoad = () => {
        if (videoRef.current) {
            setVideoDimensions({
                width: videoRef.current.videoWidth,
                height: videoRef.current.videoHeight
            });
        }
    };

    // Logo boyutunu video wrapper'a orantılı hesapla (backend ile aynı formül)
    const getLogoDisplaySize = useCallback(() => {
        const wrapper = videoWrapperRef.current;
        if (!wrapper) return 40;
        const wrapperWidth = wrapper.offsetWidth;

        // Banner GIF kontrolü (genişlik > yükseklik * 3)
        if (logoNaturalSize.width > 0 && logoNaturalSize.width > logoNaturalSize.height * 3) {
            return Math.max(40, Math.round(wrapperWidth * (watermarkSize / 100) * 0.6));
        }

        // Normal logo
        const logoWidthPercent = (watermarkSize / 100) * 20;
        return Math.max(20, Math.round(wrapperWidth * logoWidthPercent / 100));
    }, [watermarkSize, logoNaturalSize]);

    // Icon dosyası seçildiğinde
    const handleIconSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                alert('❌ Sadece resim dosyaları kullanılabilir');
                e.target.value = null;
                return;
            }

            setWatermarkFile(file);
            setSelectedBrandLogo(null);
            setDragPos(null);
            setLogoNaturalSize({ width: 0, height: 0 });

            const reader = new FileReader();
            reader.onloadend = () => {
                setWatermarkPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // Marka logosu seçildiğinde
    const handleBrandLogoSelect = async (brand, logoType) => {
        const logoUrl = logoType === 'dark' ? brand.logo_dark_url :
            logoType === 'light' ? brand.logo_light_url : brand.gif_url;
        if (!logoUrl) return;

        try {
            const fullUrl = `${BASE_URL}${logoUrl}`;

            const response = await fetch(fullUrl);
            const blob = await response.blob();
            const fileName = logoUrl.split('/').pop();
            const file = new File([blob], fileName, { type: blob.type });

            setWatermarkFile(file);
            setWatermarkPreview(fullUrl);
            setSelectedBrandLogo({ brandId: brand.id, type: logoType });
            setDragPos(null);
            setLogoNaturalSize({ width: 0, height: 0 });

            if (brand.default_wm_position) {
                setWatermarkPosition(brand.default_wm_position);
            }
            if (brand.default_wm_size) setWatermarkSize(brand.default_wm_size);
            if (brand.default_wm_opacity) setWatermarkOpacity(brand.default_wm_opacity);

            if (iconInputRef.current) {
                iconInputRef.current.value = null;
            }
        } catch (error) {
            console.error('Brand logo select error:', error);
            alert('Logo yüklenirken hata oluştu');
        }
    };

    // Preset pozisyon seçildiğinde
    const handlePositionSelect = (posKey) => {
        setWatermarkPosition(posKey);
        setDragPos(null);
    };

    // --- DRAG & DROP ---
    const handleDragStart = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();

        const overlay = overlayRef.current;
        const wrapper = videoWrapperRef.current;
        if (!overlay || !wrapper) return;

        const overlayRect = overlay.getBoundingClientRect();
        const wrapperRect = wrapper.getBoundingClientRect();

        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;

        setDragOffset({
            x: clientX - overlayRect.left,
            y: clientY - overlayRect.top
        });

        // İlk sürüklemede mevcut pozisyonu piksel olarak al
        if (!dragPos) {
            setDragPos({
                x: overlayRect.left - wrapperRect.left,
                y: overlayRect.top - wrapperRect.top
            });
        }

        setIsDragging(true);
    }, [dragPos]);

    const handleDragMove = useCallback((e) => {
        if (!isDragging) return;
        e.preventDefault();

        const wrapper = videoWrapperRef.current;
        const overlay = overlayRef.current;
        if (!wrapper || !overlay) return;

        const wrapperRect = wrapper.getBoundingClientRect();
        const overlayWidth = overlay.offsetWidth;
        const overlayHeight = overlay.offsetHeight;

        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;

        let newX = clientX - wrapperRect.left - dragOffset.x;
        let newY = clientY - wrapperRect.top - dragOffset.y;

        // Sınırları kontrol et - video dışına çıkmasın
        const maxX = wrapperRect.width - overlayWidth;
        const maxY = wrapperRect.height - overlayHeight;

        newX = Math.max(0, Math.min(newX, maxX));
        newY = Math.max(0, Math.min(newY, maxY));

        setDragPos({ x: newX, y: newY });
        setWatermarkPosition('custom');
    }, [isDragging, dragOffset]);

    const handleDragEnd = useCallback(() => {
        setIsDragging(false);
    }, []);

    // Global mouse/touch event listeners
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleDragMove);
            document.addEventListener('mouseup', handleDragEnd);
            document.addEventListener('touchmove', handleDragMove, { passive: false });
            document.addEventListener('touchend', handleDragEnd);
        }

        return () => {
            document.removeEventListener('mousemove', handleDragMove);
            document.removeEventListener('mouseup', handleDragEnd);
            document.removeEventListener('touchmove', handleDragMove);
            document.removeEventListener('touchend', handleDragEnd);
        };
    }, [isDragging, handleDragMove, handleDragEnd]);

    // Icon kaldır
    const handleRemoveIcon = () => {
        setWatermarkFile(null);
        setWatermarkPreview(null);
        setSelectedBrandLogo(null);
        setDragPos(null);
        setLogoNaturalSize({ width: 0, height: 0 });
        if (iconInputRef.current) {
            iconInputRef.current.value = null;
        }
    };

    // Kaydet
    const handleSave = () => {
        if (!watermarkPreview || !watermarkFile) {
            alert('❌ Önce bir logo seçmelisiniz');
            return;
        }

        const watermarkData = {
            file: watermarkFile,
            preview: watermarkPreview,
            position: watermarkPosition,
            size: watermarkSize,
            opacity: watermarkOpacity
        };

        // Custom pozisyon varsa, normalize edilmiş yüzde gönder (0-100)
        if (dragPos && watermarkPosition === 'custom') {
            const wrapper = videoWrapperRef.current;
            const overlay = overlayRef.current;
            if (wrapper && overlay) {
                const wrapperWidth = wrapper.offsetWidth;
                const wrapperHeight = wrapper.offsetHeight;
                const overlayWidth = overlay.offsetWidth;
                const overlayHeight = overlay.offsetHeight;

                const maxX = wrapperWidth - overlayWidth;
                const maxY = wrapperHeight - overlayHeight;

                watermarkData.position = 'custom';
                watermarkData.customX = maxX > 0 ? Math.round((dragPos.x / maxX) * 10000) / 100 : 0;
                watermarkData.customY = maxY > 0 ? Math.round((dragPos.y / maxY) * 10000) / 100 : 0;
            }
        }

        onSave(watermarkData);
        onClose();
    };

    if (!isOpen) return null;

    const videoUrl = mediaPreview?.url || mediaPreview;

    if (!videoUrl) {
        return (
            <div className="vw-modal-overlay" onClick={onClose}>
                <div className="vw-modal-container vw-modal-error" onClick={(e) => e.stopPropagation()}>
                    <div className="vw-error-content">
                        <span className="vw-error-icon">⚠️</span>
                        <p>Video yüklenemedi</p>
                        <button onClick={onClose} className="vw-btn vw-btn-secondary">Kapat</button>
                    </div>
                </div>
            </div>
        );
    }

    // Pozisyon grid
    const positions = [
        { key: 'top-left', label: 'Sol Üst' },
        { key: 'top-center', label: 'Orta Üst' },
        { key: 'top-right', label: 'Sağ Üst' },
        { key: 'center-left', label: 'Sol Orta' },
        { key: 'center', label: 'Orta' },
        { key: 'center-right', label: 'Sağ Orta' },
        { key: 'bottom-left', label: 'Sol Alt' },
        { key: 'bottom-center', label: 'Orta Alt' },
        { key: 'bottom-right', label: 'Sağ Alt' }
    ];

    const currentPositionLabel = watermarkPosition === 'custom'
        ? 'Özel Konum'
        : (positions.find(p => p.key === watermarkPosition)?.label || 'Sağ Alt');

    const logoDisplayWidth = getLogoDisplaySize();

    return (
        <div className="vw-modal-overlay" onClick={onClose}>
            <div className="vw-modal-container" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="vw-modal-header">
                    <div className="vw-header-title">
                        <span className="vw-header-icon">🎬</span>
                        <h3>Video Logo Ekle</h3>
                    </div>
                    <button className="vw-close-btn" onClick={onClose}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Content */}
                <div className="vw-modal-content">
                    {/* Sol Panel - Video Önizleme */}
                    <div className="vw-preview-panel" ref={containerRef}>
                        <div className="vw-video-wrapper" ref={videoWrapperRef}>
                            <video
                                ref={videoRef}
                                src={videoUrl}
                                controls
                                className="vw-video"
                                onLoadedMetadata={handleVideoLoad}
                            />

                            {/* Logo Overlay - Sürüklenebilir */}
                            {watermarkPreview && (
                                <div
                                    ref={overlayRef}
                                    className={`vw-logo-overlay ${dragPos ? '' : `vw-pos-${watermarkPosition}`} ${isDragging ? 'vw-dragging' : ''}`}
                                    style={dragPos ? {
                                        opacity: watermarkOpacity / 100,
                                        left: `${dragPos.x}px`,
                                        top: `${dragPos.y}px`,
                                        cursor: isDragging ? 'grabbing' : 'grab',
                                        userSelect: 'none',
                                        zIndex: 10
                                    } : {
                                        opacity: watermarkOpacity / 100,
                                        cursor: 'grab',
                                        userSelect: 'none',
                                        zIndex: 10
                                    }}
                                    onMouseDown={handleDragStart}
                                    onTouchStart={handleDragStart}
                                >
                                    <img
                                        src={watermarkPreview}
                                        alt="Logo"
                                        draggable={false}
                                        onLoad={(e) => {
                                            setLogoNaturalSize({
                                                width: e.target.naturalWidth,
                                                height: e.target.naturalHeight
                                            });
                                        }}
                                        style={{
                                            width: `${logoDisplayWidth}px`,
                                            height: 'auto',
                                            pointerEvents: 'none'
                                        }}
                                    />
                                </div>
                            )}
                        </div>

                        <div className="vw-video-meta">
                            {videoDimensions.width > 0 && (
                                <div className="vw-video-info">
                                    {videoDimensions.width} × {videoDimensions.height}
                                </div>
                            )}
                            {watermarkPreview && (
                                <div className="vw-drag-hint">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M5 9l-3 3 3 3M9 5l3-3 3 3M15 19l-3 3-3-3M19 9l3 3-3 3M2 12h20M12 2v20" />
                                    </svg>
                                    Logoyu sürükleyerek konumlandırabilirsiniz
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Sağ Panel - Ayarlar */}
                    <div className="vw-settings-panel">
                        {/* Logo Seçimi */}
                        <div className="vw-section">
                            <div className="vw-section-header">
                                <span className="vw-section-icon">🖼️</span>
                                <span className="vw-section-title">Logo Seç</span>
                            </div>

                            <div className="vw-logo-selector">
                                <input
                                    ref={iconInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleIconSelect}
                                    id="vw-logo-input"
                                    hidden
                                />

                                {!watermarkPreview ? (
                                    <label htmlFor="vw-logo-input" className="vw-logo-dropzone">
                                        <div className="vw-dropzone-icon">
                                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                <path d="M12 5v14M5 12h14" />
                                            </svg>
                                        </div>
                                        <span className="vw-dropzone-text">Logo seçmek için tıklayın</span>
                                        <span className="vw-dropzone-hint">PNG, JPG, SVG, GIF</span>
                                    </label>
                                ) : (
                                    <div className="vw-logo-preview">
                                        <img src={watermarkPreview} alt="Seçilen Logo" />
                                        <button
                                            className="vw-logo-remove"
                                            onClick={handleRemoveIcon}
                                            title="Logoyu Kaldır"
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M18 6L6 18M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Kayıtlı Markalar */}
                        {brands.length > 0 && (
                            <div className="vw-section">
                                <div className="vw-section-header">
                                    <span className="vw-section-icon">🏷️</span>
                                    <span className="vw-section-title">Kayıtlı Markalar</span>
                                </div>

                                <div className="vw-brands-list">
                                    {loadingBrands ? (
                                        <div className="vw-brands-loading">
                                            <span>Yükleniyor...</span>
                                        </div>
                                    ) : (
                                        brands.map(brand => (
                                            <div key={brand.id} className="vw-brand-item">
                                                <span className="vw-brand-name">{brand.name}</span>
                                                <div className="vw-brand-logos">
                                                    {brand.logo_dark_url && (
                                                        <button
                                                            className={`vw-brand-logo-btn dark ${selectedBrandLogo?.brandId === brand.id &&
                                                                selectedBrandLogo?.type === 'dark' ? 'selected' : ''
                                                                }`}
                                                            onClick={() => handleBrandLogoSelect(brand, 'dark')}
                                                            title="Koyu arka plan logosu"
                                                        >
                                                            <img
                                                                src={`${BASE_URL}${brand.logo_dark_url}`}
                                                                alt={`${brand.name} - Koyu`}
                                                            />
                                                        </button>
                                                    )}
                                                    {brand.logo_light_url && (
                                                        <button
                                                            className={`vw-brand-logo-btn light ${selectedBrandLogo?.brandId === brand.id &&
                                                                selectedBrandLogo?.type === 'light' ? 'selected' : ''
                                                                }`}
                                                            onClick={() => handleBrandLogoSelect(brand, 'light')}
                                                            title="Açık arka plan logosu"
                                                        >
                                                            <img
                                                                src={`${BASE_URL}${brand.logo_light_url}`}
                                                                alt={`${brand.name} - Açık`}
                                                            />
                                                        </button>
                                                    )}
                                                    {brand.gif_url && (
                                                        <button
                                                            className={`vw-brand-logo-btn gif ${selectedBrandLogo?.brandId === brand.id &&
                                                                selectedBrandLogo?.type === 'gif' ? 'selected' : ''
                                                                }`}
                                                            onClick={() => handleBrandLogoSelect(brand, 'gif')}
                                                            title="GIF watermark"
                                                        >
                                                            <img
                                                                src={`${BASE_URL}${brand.gif_url}`}
                                                                alt={`${brand.name} - GIF`}
                                                            />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Pozisyon Seçimi */}
                        {watermarkPreview && (
                            <>
                                <div className="vw-section">
                                    <div className="vw-section-header">
                                        <span className="vw-section-icon">📍</span>
                                        <span className="vw-section-title">Pozisyon</span>
                                        <span className="vw-section-value">{currentPositionLabel}</span>
                                    </div>

                                    <div className="vw-position-grid">
                                        {positions.map(pos => (
                                            <button
                                                key={pos.key}
                                                className={`vw-position-btn ${watermarkPosition === pos.key ? 'active' : ''}`}
                                                onClick={() => handlePositionSelect(pos.key)}
                                                title={pos.label}
                                            >
                                                <span className="vw-position-dot"></span>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Boyut */}
                                <div className="vw-section">
                                    <div className="vw-section-header">
                                        <span className="vw-section-icon">📏</span>
                                        <span className="vw-section-title">Boyut</span>
                                        <span className="vw-section-value">{watermarkSize}%</span>
                                    </div>

                                    <div className="vw-slider-container">
                                        <input
                                            type="range"
                                            min="30"
                                            max="200"
                                            value={watermarkSize}
                                            onChange={(e) => {
                                                setWatermarkSize(Number(e.target.value));
                                                setDragPos(null);
                                                if (watermarkPosition === 'custom') {
                                                    setWatermarkPosition('bottom-right');
                                                }
                                            }}
                                            className="vw-slider"
                                        />
                                        <div className="vw-slider-labels">
                                            <span>Küçük</span>
                                            <span>Büyük</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Opaklık */}
                                <div className="vw-section">
                                    <div className="vw-section-header">
                                        <span className="vw-section-icon">💎</span>
                                        <span className="vw-section-title">Opaklık</span>
                                        <span className="vw-section-value">{watermarkOpacity}%</span>
                                    </div>

                                    <div className="vw-slider-container">
                                        <input
                                            type="range"
                                            min="10"
                                            max="100"
                                            value={watermarkOpacity}
                                            onChange={(e) => setWatermarkOpacity(Number(e.target.value))}
                                            className="vw-slider"
                                        />
                                        <div className="vw-slider-labels">
                                            <span>Şeffaf</span>
                                            <span>Opak</span>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="vw-modal-footer">
                    <button className="vw-btn vw-btn-secondary" onClick={onClose}>
                        İptal
                    </button>
                    <button
                        className="vw-btn vw-btn-primary"
                        onClick={handleSave}
                        disabled={!watermarkPreview}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
                            <polyline points="17,21 17,13 7,13 7,21" />
                            <polyline points="7,3 7,8 15,8" />
                        </svg>
                        Kaydet
                    </button>
                </div>
            </div>
        </div>
    );
}

export default VideoWatermark;